<template>
  <div class="compose">
    <button
      v-if="types.length"
      type="button"
      class="compose-icon"
      :class="{ 'compose-icon--on': tags.length }"
      :disabled="sending"
      title="Tag records"
      @click="picking = true"
    >
      <i class="fe fe-file-text"></i>
      <span v-if="tags.length" class="compose-count">{{ tags.length }}</span>
    </button>

    <textarea
      ref="text"
      v-model="text"
      class="compose-text"
      rows="1"
      maxlength="4000"
      aria-label="Message"
      :placeholder="placeholder"
      :disabled="sending"
      @input="grow"
      @keydown.enter.exact.prevent="submit"
    ></textarea>

    <button type="button" class="compose-send" :disabled="sending || !canSend" title="Send" @click="submit">
      <span v-if="sending" class="spinner-border spinner-border-sm"></span>
      <i v-else class="fe fe-send"></i>
    </button>

    <TagPickerModal v-if="picking" v-model="tags" :types="types" @close="closePicker" />
  </div>
</template>

<script>
import TagPickerModal from "./TagPickerModal.vue";

// Chat input: text, a tag icon (opens the tag modal) and send. Enter sends,
// Shift+Enter adds a line. Tagged records go to the API as [[type:id]] tokens
// at the end of the body; the chat shows them behind a tag icon, not in the text.
export default {
  name: "ComposeBox",
  components: { TagPickerModal },
  props: {
    types: { type: Array, default: () => [] },
    sending: { type: Boolean, default: false },
    placeholder: { type: String, default: "Type a message" },
  },
  data() {
    return { text: "", tags: [], picking: false };
  },
  computed: {
    canSend() {
      return this.text.trim().length > 0 || this.tags.length > 0;
    },
  },
  methods: {
    grow() {
      const el = this.$refs.text;
      if (!el) return;
      el.style.height = "auto";
      el.style.height = Math.min(el.scrollHeight, 140) + "px";
    },
    closePicker() {
      this.picking = false;
      this.$nextTick(this.focus);
    },
    submit() {
      if (this.sending || !this.canSend) return;
      const tokens = this.tags.map((t) => `[[${t.type}:${t.id}]]`).join(" ");
      const body = [this.text.trim(), tokens].filter(Boolean).join("\n");
      this.$emit("submit", { body, tags: this.tags.map((t) => ({ type: t.type, id: t.id })) });
    },
    /** Parent calls this after a successful send. */
    reset() {
      this.text = "";
      this.tags = [];
      this.$nextTick(this.grow);
    },
    focus() {
      this.$refs.text && this.$refs.text.focus();
    },
  },
};
</script>

<style scoped>
.compose {
  display: flex;
  align-items: flex-end;
  gap: 8px;
}
.compose-text {
  flex: 1;
  resize: none;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 9px 16px;
  font-size: 0.92rem;
  line-height: 1.4;
  max-height: 140px;
  outline: none;
  background: #fff;
}
.compose-text:focus {
  border-color: #93c5fd;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}
.compose-icon,
.compose-send {
  position: relative;
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.05rem;
}
.compose-icon {
  background: #f1f5f9;
  color: #475569;
}
.compose-icon:hover,
.compose-icon--on {
  background: #eef2ff;
  color: #2563eb;
}
.compose-send {
  background: #2563eb;
  color: #fff;
}
.compose-send:disabled {
  background: #cbd5e1;
}
.compose-count {
  position: absolute;
  top: -2px;
  right: -2px;
  min-width: 17px;
  height: 17px;
  border-radius: 9px;
  background: #2563eb;
  color: #fff;
  font-size: 0.65rem;
  font-weight: 700;
  line-height: 17px;
  padding: 0 4px;
}
</style>
